"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  areStringArraysEqual,
  reconcileVisibleSeriesKeys,
  resolveInitialVisibleSeriesKeys,
  toggleVisibleSeriesKeys,
} from "./series-visibility";
import type { VizExportFormat } from "./viz-actions";
import { VizActionBar, VizExportMenu, VizSeriesMenu } from "./viz-actions";

const DEFAULT_CHART_HEIGHT = 320;
const MOBILE_DEFAULT_CHART_HEIGHT = 300;
const MIN_CHART_HEIGHT = 280;
const MAX_CHART_HEIGHT = 420;
const DENSE_X_AXIS_EXTRA_HEIGHT = 28;

type SeriesToggleMode = "single-metric" | "multi-metric";

/**
 * 時系列チャートの高さを、X 軸の密度込みで読みやすい範囲へ丸める。
 *
 * @remarks
 * AI 可視化カードは本文と連続表示されるため、縦長化を抑えつつも
 * 回転ラベルや比較 2 行 tick が潰れない高さを renderer 側で確保する。
 */
export function resolveTimeSeriesChartHeight(args: {
  chartHeight: number | null | undefined;
  hasDenseXAxis: boolean;
  isMobile: boolean;
}): number {
  const requestedHeight = args.chartHeight;
  if (typeof requestedHeight === "number") {
    return Math.max(
      MIN_CHART_HEIGHT,
      Math.min(MAX_CHART_HEIGHT, Math.round(requestedHeight)),
    );
  }

  const baseHeight = args.isMobile
    ? MOBILE_DEFAULT_CHART_HEIGHT
    : DEFAULT_CHART_HEIGHT;
  const computedHeight = args.hasDenseXAxis
    ? baseHeight + DENSE_X_AXIS_EXTRA_HEIGHT
    : baseHeight;

  return Math.max(MIN_CHART_HEIGHT, Math.min(MAX_CHART_HEIGHT, computedHeight));
}

function resolveSeriesToggleMode(
  mode: "single-metric" | "multi-metric" | null | undefined,
): SeriesToggleMode {
  return mode === "single-metric" ? "single-metric" : "multi-metric";
}

export function useTimeSeriesVisibleSeries(args: {
  allSeriesKeys: string[];
  defaultVisibleSeriesKeys?: string[] | null;
  seriesToggleMode?: "single-metric" | "multi-metric" | null;
}): {
  visibleSeriesKeys: string[];
  visibleSeriesKeySet: Set<string>;
  toggleSeriesVisibility: (key: string, checked?: boolean) => void;
  resolvedSeriesToggleMode: SeriesToggleMode;
} {
  const resolvedSeriesToggleMode = resolveSeriesToggleMode(
    args.seriesToggleMode,
  );

  const [visibleSeriesKeys, setVisibleSeriesKeys] = useState<string[]>(() => {
    const initial = resolveInitialVisibleSeriesKeys({
      allSeriesKeys: args.allSeriesKeys,
      defaultVisibleSeriesKeys: args.defaultVisibleSeriesKeys,
    });
    if (resolvedSeriesToggleMode === "single-metric") {
      const first = initial[0];
      return first ? [first] : [];
    }
    return initial;
  });

  useEffect(() => {
    setVisibleSeriesKeys((current) => {
      const next = reconcileVisibleSeriesKeys({
        current,
        allSeriesKeys: args.allSeriesKeys,
      });
      const normalized =
        resolvedSeriesToggleMode === "single-metric" && next.length > 1
          ? [next[0]!]
          : next;
      return areStringArraysEqual(normalized, current) ? current : normalized;
    });
  }, [args.allSeriesKeys, resolvedSeriesToggleMode]);

  const toggleSeriesVisibility = useCallback(
    (key: string, checked?: boolean) => {
      setVisibleSeriesKeys((current) => {
        if (resolvedSeriesToggleMode === "single-metric") {
          const isVisible = current.includes(key);
          const shouldShow = checked ?? !isVisible;
          if (!shouldShow) {
            // Legend click から all-hidden を作れるようにして、single-series でも
            // 系列ラベルと表示状態の操作体系を multi-metric と揃える。
            return current.filter((currentKey) => currentKey !== key);
          }
          const next = [key];
          return areStringArraysEqual(next, current) ? current : next;
        }

        const updated = toggleVisibleSeriesKeys({
          current,
          allSeriesKeys: args.allSeriesKeys,
          key,
          checked,
        });
        return areStringArraysEqual(updated, current) ? current : updated;
      });
    },
    [args.allSeriesKeys, resolvedSeriesToggleMode],
  );

  const visibleSeriesKeySet = useMemo(
    () => new Set(visibleSeriesKeys),
    [visibleSeriesKeys],
  );

  return {
    visibleSeriesKeys,
    visibleSeriesKeySet,
    toggleSeriesVisibility,
    resolvedSeriesToggleMode,
  };
}

export function TimeSeriesActionBar(args: {
  allowSeriesToggle?: boolean | null;
  allowExport?: boolean | null;
  seriesMenuItems: Array<{ key: string; label: string }>;
  visibleKeySet: Set<string>;
  onToggleSeries: (key: string, checked?: boolean) => void;
  exportFormats: VizExportFormat[];
  onExport: (format: VizExportFormat) => void | Promise<void>;
  onCopyImage?: (() => void | Promise<void>) | null;
  hasChartData: boolean;
  alwaysRender?: boolean;
}) {
  const resolvedAllowSeriesToggle = args.allowSeriesToggle !== false;
  const resolvedAllowExport = args.allowExport !== false;

  if (
    !args.alwaysRender &&
    !resolvedAllowSeriesToggle &&
    !resolvedAllowExport
  ) {
    return null;
  }

  return (
    <VizActionBar>
      {resolvedAllowSeriesToggle ? (
        <VizSeriesMenu
          series={args.seriesMenuItems}
          visibleKeySet={args.visibleKeySet}
          onToggle={(key, checked) => {
            args.onToggleSeries(key, checked);
          }}
        />
      ) : null}
      {resolvedAllowExport ? (
        <VizExportMenu
          formats={args.exportFormats}
          onExport={args.onExport}
          onCopyImage={args.onCopyImage}
          disabled={!args.hasChartData}
        />
      ) : null}
    </VizActionBar>
  );
}
